// ===== src/pages/admin/AdminCars/index.jsx =====
import { useEffect, useMemo, useState, useCallback } from "react";
import AdminTable from "../../../components/AdminTable";
import { API_DOMAIN } from "config";
import { formatDate } from "../../../utils/formatDate";

function shekel(n) {
  if (n == null) return "";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  });
}

const offerStatusMap = {
  0: { txt: "ממתין להצעה", clr: "black" },
  1: { txt: "ממתין לאישור", clr: "#CE0000" },
  2: { txt: "מאושר", clr: "#619D1C" },
  3: { txt: "הצעה שנדחתה", clr: "#666666" },
  4: { txt: "ארכיון", clr: "#666666" },
  5: { txt: "לא מעונין", clr: "#666666" },
};

function OfferStatusBadge({ value }) {
  const s = offerStatusMap[Number(value) || 0] || offerStatusMap[0];
  return (
    <span
      className="inline-flex items-center justify-center rounded-[14px] font-medium text-white"
      style={{
        backgroundColor: s.clr,
        color: "white",
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 14,
        paddingRight: 14,
        minWidth: 120,
        lineHeight: 1,
      }}
    >
      {s.txt}
    </span>
  );
}

function shekelNum(n) {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (Number.isNaN(num)) return "";
  return num.toLocaleString("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  });
}

function offerRangeText(r) {
  const min = r.min_offer ?? null;
  const max = r.max_offer ?? null;
  if (min == null && max == null) return "";
  if (min != null && max != null && min !== max) return `${shekelNum(min)} - ${shekelNum(max)}`;
  return shekelNum(min ?? max);
}

export default function AdminCars() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // ספקים
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState(""); // "" = כולם

  const loadProviders = useCallback(async () => {
    try {
      const res = await fetch(`${API_DOMAIN}/admin/getUsers?type=providers`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setProviders(list.map((p) => ({ id: Number(p.id), name: p.name || `#${p.id}` })));
    } catch (e) {
      console.error(e);
      setProviders([]);
    }
  }, []);

  const loadCars = useCallback(async (pid) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (pid) qs.set("provider_id", pid);

      const url = qs.toString()
        ? `${API_DOMAIN}/admin/getCars?${qs.toString()}`
        : `${API_DOMAIN}/admin/getCars`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    loadCars(providerId);
  }, [loadCars, providerId]);

  const columns = useMemo(
    () => [
      { name: "ID", selector: (r) => r.id, width: "80px", sortable: true },
      { name: "מס' רכב", selector: (r) => r.license_plate, width: "120px", sortable: true },
      { name: "שנה", selector: (r) => r.year, width: "90px", sortable: true },
      { name: "יצרן", selector: (r) => r.manufacturer, sortable: true },
      { name: "דגם", selector: (r) => r.model, sortable: true },

      {
        name: "מחיר מבוקש",
        selector: (r) => r.asking_price,
        sortable: true,
        cell: (r) => <span style={{ fontWeight: 700 }}>{shekel(r.asking_price)}</span>,
      },

      {
        name: "תאריך יצירה",
        selector: (r) => r.creation_date,
        sortable: true,
        width: "160px",
        cell: (r) => formatDate(r.creation_date),
      },

      { name: "ספק", selector: (r) => r.provider_name, sortable: true, wrap: true },

      {
        name: "סטטוס הצעה",
        selector: (r) => r.offer_status,
        sortable: true,
        width: "170px",
        cell: (r) => <OfferStatusBadge value={r.offer_status} />,
      }

    ],
    []
  );

  const searchKeys = ["id", "license_plate", "manufacturer", "model", "sub_model", "year", "provider_name"];

  const exportKeys = [
    "id",
    "license_plate",
    "year",
    "manufacturer",
    "model",
    "sub_model",
    "asking_price",
    "status",
    "is_archive",
    "creation_date",
    "provider_name",
    "offer_status",
    "min_offer",
    "max_offer",
  ];

  return (
    <div className="p-4 bg-slate-100 min-h-[calc(100vh-4rem)]" dir="rtl">
      <AdminTable
        title="רכבים"
        rows={rows}
        columns={columns}
        searchKeys={searchKeys}
        exportKeys={exportKeys}
        loading={loading}
        // ✅ פילטר ליד החיפוש (בפס הכהה)
filters={
  <div className="relative w-[260px] max-w-xs">
   <select
  value={providerId}
  onChange={(e) => setProviderId(e.target.value)}
  className="admin-select bg-white border border-slate-300 rounded-full pr-8 pl-3 py-1.5 text-xs text-slate-800
             focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 w-full
             text-right"
>

      <option value="">כל הספקים</option>
      {providers.map((p) => (
        <option key={p.id} value={String(p.id)}>
          {p.name} 
        </option>
      ))}
    </select>

    {/* חץ כמו אייקון (בדיוק כמו שאת עושה בחיפוש עם 🔍) */}
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[11px]">
      ▼
    </span>
  </div>
}

      />
    </div>
  );
}
